import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Stack,
    Fade,
    Modal,
    Grid,
    Typography,
    Button,
    Popover,
    TextField,
    InputAdornment,
    List,
    ListItemIcon,
    ListItem,
    ListItemButton,
    ListItemText,
    FormControl,
    Select,
    MenuItem,
    FormControlLabel,
    Radio,
    RadioGroup,
    Divider,
    FormGroup,
    Checkbox
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add'
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import hashFill from '@iconify/icons-eva/hash-fill';
import { Icon } from '@iconify/react';

import { ReactComponent as InputSearchIcon } from "../../../assets/icons/icon-feather-search.svg"
import { ReactComponent as DropDownIcon } from "../../../assets/icons/icon-angle-down.svg"
import ReportFilterFormNew from '../ReportFilterFormNew';
import { FILTER_TYPE_NEW, NUMBER_FILTER_TYPE } from '../../../constants/filterType';
import { useDispatch, useSelector } from 'react-redux';
import { reportActions } from '../../../redux/reducers/report/reportReducer';
import { CustomSwitch, removeReference } from '../../../utils/Reusables';
import ReportViewFilters from '../ReportViewFilters';
import { ReactComponent as IconFilterDelete } from "../../../assets/icons/icon-delete2-small.svg"
import { ReactComponent as IconFilterDrag } from "../../../assets/icons/icon-report-filter-drag.svg"
import { ReactComponent as IconFilterSetting } from "../../../assets/icons/icon-report-filter-setting.svg"
import { ReactComponent as CloseIcon } from "../../../assets/icons/icon_close.svg";

const useStyles = makeStyles((theme) => ({
    bgWhite: {
        backgroundColor: '#fff',
        borderRadius: '4px',
    },
    formInput: {
        paddingLeft: 0,
        '&::placeholder': {
            paddingLeft: '-10px',
        }
    },
    searchFieldListItem: {
        paddingTop: '0.6rem',
        paddingBottom: '0.6rem',
        '&:hover': {
            background: "#f1f1f1",
            backgroundColor: "#f1f1f1",
            zIndex: 9,
            "& $listItemDescription": {
                visibility: 'visible'
            },
            "& $searchFieldListMoreIcon": {
                visibility: 'visible'
            }
        },

    },
    listItemDescription: {
        visibility: 'visible',
        '&:hover': {
            visibility: 'visible'
        }
    },
    searchFieldListMoreIcon: {
        visibility: 'visible',
        '&:hover': {
            visibility: 'visible'
        }
    }
}));

const Accordion = styled((props) => (
    <MuiAccordion disableGutters={true} TransitionProps={{ timeout: { appear: 1, enter: 1, exit: 4 } }} 
        elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }}  />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const ReportFilterTab = ({ query, setQuery, currentDataset }) => {
    const buttonRef = useRef();
    const classes = useStyles()
    const [anchorAddFilter, setAnchorAddFilter] = useState(null);
    const [searchMeasure, setSearchMeasure] = useState(null);
    const [filterButtonWidth, setFilterButtonWidth] = useState('200px')
    const [filterFormOpen, setFilterFormFilter] = useState(-1);
    const [measureList, setMeasureList] = useState([...currentDataset.measures, ...currentDataset.dimensions])
    const [deleteAllPopup, setDeleteAllPopup] = useState(false)
    const [expanded, setExpanded] = useState(false);
    const [isSetnewFilter, setIsSetNewFilter] = useState(false);
    const [addToReportPopup, setAddToReportPopup] = useState(false);
    const [addToReportFilterCategory, setAddToReportFilterCategory] = useState(null);
    const [addToReportFilterCustomFilterList, setAddToReportFilterCustomFilterList] = useState([]);

    const reportDetails = useSelector(state => state.reportDetails)
    const dispatch = useDispatch()
    //console.log(reportDetails)

    //const filterList = reportDetails.filters ?? []

    const onAddFilter = useCallback((event) => {
        let obj = {
            type: null,
            member: null,
            displayName: null,
            operator: null,
            values: [],
            fieldType: 'Dropdown',
            cascaded: false,
            cascadedWith: null,
            onReport: false,
            orderPriority: 0,
            showSelectAll: false,
            loader: false,
            fieldType: ''
        }
        dispatch(reportActions.addFilter({ filter: obj }))
    })

    useEffect(() => {
        if(!isSetnewFilter) {
            setTimeout(() => {
                if(!(reportDetails?.filters && reportDetails?.filters.length > 0)) {
                    onAddFilter();
                    setExpanded('0-applied-filters')
                }
                setIsSetNewFilter(true);
            });
        }
    }, [isSetnewFilter])

    const handleClose = useCallback(() => {
        setAnchorAddFilter(null);
    })

    const handleSearchMeasure = useCallback((event) => {
        setSearchMeasure(event.target.value.toLowerCase());
    })

    const onSelectMeasure = useCallback((measure) => {
        //console.log(measure)
        let details = removeReference(reportDetails)
        let obj = {
            type: measure?.type,
            member: currentDataset?.dataSetName + "." + measure?.name,
            displayName: measure?.displayName,
            operator: measure?.type === "time" ? 'inDateRange' : 'equals',
            values: [],
            fieldType: 'Dropdown',
            cascaded: false,
            cascadedWith: null,
            onReport: false,
            orderPriority: 0,
            showSelectAll: false,
            loader: false,
            fieldType: measure?.dataSetType
        }
        if (!details.filters) {
            details.filters = []
        }
        details.filters.unshift(obj)
        dispatch(reportActions.updateFilterList({ list: [...details.filters] }))
        //updateReportDetails({...reportDetails,filters:[...reportDetails.filters]})
        setAnchorAddFilter(null);
    })

    const updateFilterObj = useCallback((obj, index) => {
        let details = removeReference(reportDetails)
        if (obj) {
            dispatch(reportActions.updateFilterByIndex({ filter: obj, index: index }));
        } else {
            dispatch(reportActions.deleteFilterByIndex({ index: index }));
        }
        if (details.filters.filter(obj => !obj.onReport).length < 1) {
            setQuery({ ...query, filters: [] })
        }
    })

    const handleChange = (panel) => (event, newExpanded) => {
        if(expanded === panel) {
            setExpanded(false);
        } else {
            setExpanded(panel ?? false);
        }
    };

    const openEditReportFilter = useCallback((filter, i) => {
        setFilterFormFilter(filterFormOpen === i ? -1 : i);
    })

    const applyFilter = useCallback(() => {
        let filters = reportDetails.filters.filter(obj => !obj.onReport && (obj.values.length > 0 || ['set', 'notSet'].indexOf(obj.operator) > -1)).map(obj => {
            if(obj.extraFilters){
                let extraFilterObj = {
                    [obj.conditionType]:[]       
                }
                for(let extFiltObj of obj.extraFilters){
                  extraFilterObj[obj.conditionType].push({
                    member: extFiltObj.member,
                    operator: extFiltObj.operator.split('#')[0],
                    values: extFiltObj.values
                  })
                }
                extraFilterObj[obj.conditionType].push({
                    member: obj.member,
                    operator: obj.operator.split('#')[0],
                    values: obj.values
                })
                return extraFilterObj
            }else{
                return {
                    member: obj.member,
                    operator: obj.operator.split('#')[0],
                    values: obj.values
                }
            }            
        })
        //console.log(filters)
        setQuery({ ...query, filters })
    })

    const deleteAllFilter = useCallback(() => {
        setDeleteAllPopup(false)
        dispatch(reportActions.updateFilterList({ list: [] }))
    })

    const handleAddToReportFilterCustomFilterList = (e) => {
        let updatedValue;
        if(addToReportFilterCustomFilterList?.includes(e.target.value)) {
            updatedValue = addToReportFilterCustomFilterList.toSpliced(addToReportFilterCustomFilterList?.indexOf(e.target.value), 1);
        } else {
            if(addToReportFilterCustomFilterList?.length > 0){
                updatedValue = [...addToReportFilterCustomFilterList, e.target.value];
            } else {
                updatedValue = [e.target.value];
            }
            
        }
        setAddToReportFilterCustomFilterList(updatedValue);
        
    }

    return <Box className="report-filters-section">
        <Typography variant="h3" component="h3" className="form_input_label font-weight-500" sx={{mb: 1.25}}>Filters</Typography>
        <Grid container alignItems="center" spacing={2.5}>
            
            {/* <Grid item xs={12}>
                <Box className="re-re-new-filters-listitem">
                    <Accordion className="re-re-new-filters-listitem-accordion" 
                        expanded={true} onChange={handleChange(`applied-filters-index-0`)}>
                        <AccordionSummary aria-controls={`applied-filters-content`} 
                            id={`applied-filters-header`}>
                            <Box className="re-flex-between">
                                <Typography component={"h4"} className='re-re-new-filters-listitem-header'>
                                    {'New Filter'}
                                </Typography>
                                <Box className="re-flex-align-center">
                                    <Typography className='re-anchor-light-small' component={'span'}>Show on report</Typography>
                                    <IconFilterDelete className="re-re-new-filters-listitem-delete-filter" onClick={() => {return false}} />
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid className='field-details'>
                                <ReportFilterFormNew currentDataset={currentDataset ?? {}} filterList={reportDetails.filters ?? []} filterObj={[]} filterIndex={0} />
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Grid> */}
            
            {
                reportDetails.filters && reportDetails.filters.filter(obj => obj.onReport).length > 0 &&
                <Grid item xs={12}>
                    <Box className="re-re-new-filters-listitem 11">
                        <Typography variant="h4" component="h4" className="form_input_label font-weight-500">Filters Visible on Report</Typography>
                        <ReportViewFilters currentDataset={currentDataset}/>
                    </Box>
                </Grid>
            }
            {
                reportDetails.filters && reportDetails.filters.filter(obj => !obj.onReport).length > 0 &&
                <Grid item xs={12}>
                    <Box className="re-re-new-filters-listitem 22">
                        {
                            reportDetails.filters.map((filter, index) => {
                                return !filter.onReport ? 
                                    
                                    <Accordion className="re-re-new-filters-listitem-accordion" key={`${filter.member}-applied-filters-${index}`} 
                                        expanded={expanded === `${index}-applied-filters`} >
                                        <AccordionSummary aria-controls={`${filter?.member}-${filter?.operator}-applied-filters-content`} 
                                            id={`${filter?.member}-${filter?.operator}-applied-filters-header`} onClick={handleChange(`${index}-applied-filters`)}>
                                            <Box className="re-flex-start">
                                                <IconFilterDrag className="re-re-new-filters-listitem-drag" />
                                                <Box className="re-flex-between" data-key={`${index}-applied-filters`} >
                                                    <Typography component={"h4"} className='re-re-new-filters-listitem-header'>
                                                        {filter.displayName ?? 'New Filter'}
                                                    </Typography>
                                                    <Box className="re-flex-align-center">
                                                        <Typography className='re-re-new-filters-listitem-filter-show' component={'span'} onClick={(e) => {e.stopPropagation();setAddToReportPopup(true)}}>
                                                            {(expanded === `${index}-applied-filters`) ? `Show on report`: `Hide`}
                                                        </Typography>
                                                        <IconFilterDelete className="re-re-new-filters-listitem-delete-filter" onClick={(e) => {e.stopPropagation();return false}} />
                                                        <IconFilterSetting className="re-re-new-filters-listitem-setting-filter" onClick={(e) => {e.stopPropagation();console.log("112")}}  />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid className='field-details'>
                                                <ReportFilterFormNew currentDataset={currentDataset} filterList={reportDetails.filters} filterObj={filter} filterIndex={index} />
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                :''
                            })
                        }
                    </Box>
                </Grid>
            }

            {
                    reportDetails.filters && reportDetails.filters.length > 0 && 
                    <Grid item xs={12}>
                        <Stack direction="row" width="100%" spacing={1.25} flexShrink={0}>
                            <Button  className="re-btn re-btn-main re-btn-sm"  onClick={applyFilter} >
                                Apply all filters
                            </Button>
                            <Button  className="re-btn re-btn-outline re-btn-sm"  onClick={e => setDeleteAllPopup(true)}>
                                Delete all filters
                            </Button>
                        </Stack>
                    </Grid>
                } 

            {
                (!reportDetails.filters || reportDetails.filters.filter(obj => !obj.onReport).length < 1 || reportDetails.filters.filter(obj => !obj.member).length < 1) &&
                <Grid item xs={12}>
                    <Button className="re-btn re-btn-outline re-btn-full" variant="contained" color="primary" 
                        // startIcon={<AddIcon />} 
                        onClick={onAddFilter} ref={buttonRef} >
                        + Add New Filter
                    </Button>
                </Grid>
            }

        </Grid>
        <Popover id="measure-list" open={Boolean(anchorAddFilter)} anchorEl={anchorAddFilter} onClose={handleClose}
            PaperProps={{
                style: {
                    width: filterButtonWidth, height: 'fit-content', borderTopLeftRadius: 0,
                    borderTopRightRadius: 0, marginTop: '10px', backgroundColor: 'transparent'
                },
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
        >
            <Box width="100%" height="100%" >
                <TextField className="cm_rprt_setting_content report_form_ui_input" type="search" size="small"
                    placeholder="Search field" onChange={handleSearchMeasure}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <InputSearchIcon className="input_search_icon" />
                            </InputAdornment>
                        ),
                        classes: {
                            input: classes.formInput,
                            adornedEnd: classes.bgWhite + " form_input"
                        }
                    }}
                />
            </Box>
            <Box className="field_list report_filter_list height-fit max-width-100 mt-0" sx={{ maxHeight: '300px', backgroundColor: '#fff' }} >
                <List component="nav" aria-label="main mailbox folders" className="field_list ">
                    {
                        measureList.filter(obj => obj.displayName.toLowerCase().includes(searchMeasure) && !obj.joinHiddenField).map((measure, index) => (
                            <ListItemButton className={classes.searchFieldListItem} key={index} onClick={() => onSelectMeasure(measure)} alignItems="flex-start">
                                <ListItemIcon sx={{ mt: '0px', mr: '4px' }} className={classes.searchFieldListIcon}>
                                    {measure.fieldType === "MEASURE" ? <FunctionsOutlinedIcon className="cm_dimension_icon icon_color_main" /> : measure.type === "number" ? <Icon icon={hashFill} className="cm_measr_lbl num_icon icon_color_main" /> : measure.type === "time" ? <DateRangeIcon className="cm_measr_lbl icon_color_main" /> : <LabelOutlinedIcon className="cm_measr_lbl icon_color_main" />}
                                </ListItemIcon>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Typography className="text-plain"> {measure?.displayName} </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" className={classes.listItemDescription + " text-sub"}>{measure?.description} </Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        ))
                    }
                </List>
            </Box>
        </Popover>
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className="modal-root"
            open={deleteAllPopup}
            onClose={() => setDeleteAllPopup(false)}
            closeAfterTransition
        >
            <Fade in={deleteAllPopup}>
                <Box className="modal-paper modal-box modal-sm" id="transition-modal-title">
                    <Box className="modal-content-simple">
                        <Typography variant="subtitle1" className="modal-heading-title-simple">{`Are you sure to delete all Filters ?`}</Typography>
                        <Box className="modal-footer-simple">
                            <Button onClick={e => setDeleteAllPopup(false)} color="primary" className="report_form_ui_btn cancel mr-2" variant="outlined" > Cancel </Button>
                            <Button onClick={deleteAllFilter} color="error" autoFocus className="report_form_ui_btn submit" variant="contained"> Delete </Button>
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Modal>

        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className="modal-root"
            open={addToReportPopup}
            onClose={() => setAddToReportPopup(false)}
            closeAfterTransition
        >
            <Fade in={addToReportPopup}>
                <Box className="modal-paper modal-box modal-md" id="transition-modal-title">
                    <CloseIcon onClick={() => setAddToReportPopup(false)} className="enlarge-close enlarge-close-lg"   />
                    <Box className="modal-header p-2" style={{borderBottom: '0', marginBottom: 0}}>
                        <Typography component={'h2'} className="modal-header-title modal-header-title-lg text-center" 
                            id="simple-modal-title" style={{marginTop: '40px'}}
                        >Add to report</Typography>
                    </Box>
                    <Box className="modal-content" style={{marginTop: 0, marginBottom: 0, maxHeight: '75vh'}}>
                        <Grid container spacing={2.5} >
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Typography component="label" className='re-formgroup-label-sm'>Filter name</Typography>
                                    <TextField 
                                        className="report_form_ui_input medium-dark text-small"
                                        id="field_name"
                                        variant="outlined"
                                        value=''
                                        onChange={(val) => {return false}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Typography component="label" className='re-formgroup-label-sm'>Filter type</Typography>
                                    <Select
                                        variant="outlined"
                                        className="report_form_ui_input_select medium-dark text-small pl-0"
                                        id={"filter-type"}
                                        value={''}
                                        onChange={() => {return false}}
                                        size='small'
                                        IconComponent={DropDownIcon}
                                        MenuProps={{
                                            classes: {
                                                root: `form-select-menu re-form-select-menulist`
                                            }
                                        }}
                                    >
                                        {
                                            [
                                                {displayName: 'Filter type 1', name: 'Filter Type 1'},
                                                {displayName: 'Filter type 2', name: 'Filter Type 2'},
                                                {displayName: 'Filter type 3', name: 'Filter Type 3'}
                                            ].map(obj => (
                                                <MenuItem key={`${obj.value}`} 
                                                    className={classes.menuDropdownItem + " menu-item-small"} 
                                                    value={obj.name}>
                                                        {obj.displayName}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Typography component="label" style={{marginBottom: 0}}
                                        className='re-formgroup-label-sm' >Filter category</Typography>
                                    <RadioGroup
                                        row
                                        name="filter-category"
                                        value={addToReportFilterCategory}
                                        onChange={e => setAddToReportFilterCategory(e.target.value)}
                                    >
                                        <FormControlLabel className="re-formcontrol-label-sm"  value="single" 
                                            control={<Radio className="re-form-checkbox-sm" size="small" />} label="Single select" />
                                        <FormControlLabel className="re-formcontrol-label-sm"  value="multi" 
                                            control={<Radio className="re-form-checkbox-sm" size="small" />} label="Multi-select" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Typography component="label" style={{marginBottom: 0}}
                                        className='re-formgroup-label-sm'>Customise filter list</Typography>
                                    <FormGroup row>
                                        <FormControlLabel
                                            className="re-formcontrol-label-sm"
                                            control={
                                            <Checkbox size="small" className="re-form-checkbox-sm" 
                                                checked={addToReportFilterCustomFilterList?.includes("Select all")} 
                                                onChange={handleAddToReportFilterCustomFilterList} 
                                                name="Select all" value="Select all" />
                                            }
                                            label="Show select all"
                                        />
                                        <FormControlLabel
                                            className="re-formcontrol-label-sm"
                                            control={
                                            <Checkbox size="small" className="re-form-checkbox-sm" 
                                                checked={addToReportFilterCustomFilterList?.includes("Specific value")} 
                                                onChange={handleAddToReportFilterCustomFilterList} 
                                                name="Specific value" value="Specific value" />
                                            }
                                            label="Specific value"
                                        />
                                        
                                    </FormGroup>
                                </FormControl>
                            </Grid>

                            {
                                addToReportFilterCustomFilterList?.includes("Specific value")
                                    ?   <Grid item xs={12} style={{paddingTop: 0}}>
                                            <FormControl fullWidth>
                                                <Typography component="label" style={{fontWeight: 'normal'}}
                                                    className='re-formgroup-label-sm'>Value list</Typography>
                                                <TextField 
                                                    className="report_form_ui_input medium-dark text-small"
                                                    id="field_name"
                                                    variant="outlined"
                                                    value=''
                                                    onChange={(val) => {return false}}
                                                />
                                            </FormControl>
                                        </Grid>
                                    :   null
                            }

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            <Grid item xs={12}>
                                <Box className="re-flex-between">
                                    <Typography component="label" style={{marginBottom: 0}}
                                        className='re-formgroup-label-sm'>Cascading filter</Typography>
                                    <CustomSwitch defaultChecked={false} inputProps={{ 'aria-label': 'custom design' }} />
                                </Box>
                                
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            <Grid item xs={12}>
                                <Box className="re-flex-between">
                                    <Typography component="label" style={{marginBottom: 0}}
                                        className='re-formgroup-label-sm'>Dynamic label</Typography>
                                    <CustomSwitch defaultChecked={false} inputProps={{ 'aria-label': 'custom design' }} />
                                </Box>
                                
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                        </Grid>

                        <Divider style={{margin: '30px -30px 0'}} />
                        <Box className="modal-footer-simple">
                            <Button 
                                // onClick={handleAddReportReset} 
                                className="re-btn re-btn-outline mr-20px width-75p" variant="outlined" > Reset </Button>
                            <Button 
                                // onClick={handleAddReportSubmit}  
                                autoFocus 
                                className="re-btn re-btn-main width-75p" variant="contained"> Add </Button>
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    </Box>
}

export default memo(ReportFilterTab)